// Staff registry settings page (CLAUDE.md §9, design spec §4A).
//
// Single page with two tabs: Staff (list/edit/add/group/link) and
// Issues (data quality queue). All mutations call the api worker which
// audits each change.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  type DataQualityIssue,
  type Staff,
  type StaffGroup,
  type StaffPayload,
  type StaffSource,
  type StaffStatus,
} from '../lib/api';

type Tab = 'staff' | 'issues';

const STATUS_OPTIONS: StaffStatus[] = ['active', 'inactive', 'departed'];
const SOURCE_OPTIONS: StaffSource[] = ['axcelerate', 'ghl', 'ringcentral', 'xero', 'm365'];

export function StaffRegistry() {
  const [tab, setTab] = useState<Tab>('staff');
  return (
    <section>
      <h1>Staff registry</h1>
      <p className="lede">
        Single source of staff identity. Source-system identifiers from aXcelerate, GHL,
        RingCentral, Xero, and M365 resolve back to a canonical staff record here.
      </p>
      <nav className="tab-nav" aria-label="Staff registry tabs">
        <button
          className={tab === 'staff' ? 'tab active' : 'tab'}
          onClick={() => setTab('staff')}
        >
          Staff
        </button>
        <button
          className={tab === 'issues' ? 'tab active' : 'tab'}
          onClick={() => setTab('issues')}
        >
          Data quality issues
        </button>
      </nav>
      {tab === 'staff' ? <StaffTab /> : <IssuesTab />}
      <style>{styles}</style>
    </section>
  );
}

function StaffTab() {
  const [staff, setStaff] = useState<Staff[] | null>(null);
  const [groups, setGroups] = useState<StaffGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Staff | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const [staffRes, groupsRes] = await Promise.all([api.staff.list(), api.groups.list()]);
      setStaff(staffRes.staff);
      setGroups(groupsRes.groups);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div>
      <div className="toolbar">
        <button onClick={() => setShowAdd(true)}>+ Add staff</button>
        <span className="count">{staff ? `${staff.length} record${staff.length === 1 ? '' : 's'}` : ''}</span>
      </div>

      {error && <p className="error">{error}</p>}

      {!staff && !error && <p>Loading…</p>}

      {staff && staff.length === 0 && (
        <p className="empty">
          No staff yet. Add the first record so connectors can resolve source identifiers.
        </p>
      )}

      {staff && staff.length > 0 && (
        <table className="staff-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>Groups</th>
              <th>Source links</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.staffId}>
                <td>{s.displayName}</td>
                <td>{s.primaryEmail ?? '—'}</td>
                <td>
                  <span className={`status status-${s.status}`}>{s.status}</span>
                </td>
                <td>
                  {s.groups.length === 0 ? (
                    <span className="muted">none</span>
                  ) : (
                    s.groups.map((g) => (
                      <span key={g.groupId} className="badge">
                        {g.groupName}
                      </span>
                    ))
                  )}
                </td>
                <td>
                  {s.links.length === 0 ? (
                    <span className="muted">none</span>
                  ) : (
                    <span>{s.links.length}</span>
                  )}
                </td>
                <td>
                  <button className="secondary" onClick={() => setEditing(s)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <StaffFormDialog
          title="Add staff"
          onClose={() => setShowAdd(false)}
          onSave={async (payload) => {
            await api.staff.create(payload);
            setShowAdd(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <StaffEditDialog
          staff={editing}
          groups={groups}
          onClose={() => setEditing(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

interface StaffFormDialogProps {
  title: string;
  initial?: StaffPayload;
  onClose: () => void;
  onSave: (payload: StaffPayload) => Promise<void>;
}

function StaffFormDialog({ title, initial, onClose, onSave }: StaffFormDialogProps) {
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const [givenName, setGivenName] = useState(initial?.givenName ?? '');
  const [familyName, setFamilyName] = useState(initial?.familyName ?? '');
  const [primaryEmail, setPrimaryEmail] = useState(initial?.primaryEmail ?? '');
  const [status, setStatus] = useState<StaffStatus>(initial?.status ?? 'active');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog title={title} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setSaving(true);
          setError(null);
          try {
            await onSave({
              displayName: displayName.trim(),
              givenName: givenName.trim() || null,
              familyName: familyName.trim() || null,
              primaryEmail: primaryEmail.trim() || null,
              status,
              notes: notes.trim() || null,
            });
          } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            setSaving(false);
          }
        }}
      >
        <Field label="Display name" required>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
        </Field>
        <div className="row">
          <Field label="Given name">
            <input value={givenName ?? ''} onChange={(e) => setGivenName(e.target.value)} />
          </Field>
          <Field label="Family name">
            <input value={familyName ?? ''} onChange={(e) => setFamilyName(e.target.value)} />
          </Field>
        </div>
        <Field label="Primary email">
          <input
            type="email"
            value={primaryEmail ?? ''}
            onChange={(e) => setPrimaryEmail(e.target.value)}
          />
        </Field>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value as StaffStatus)}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Notes">
          <textarea rows={2} value={notes ?? ''} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <button type="button" className="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" disabled={saving || !displayName.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

interface StaffEditDialogProps {
  staff: Staff;
  groups: StaffGroup[];
  onClose: () => void;
  onChanged: () => Promise<void>;
}

function StaffEditDialog({ staff, groups, onClose, onChanged }: StaffEditDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [linkSource, setLinkSource] = useState<StaffSource>('axcelerate');
  const [linkId, setLinkId] = useState('');
  const [groupId, setGroupId] = useState(groups[0]?.groupId ?? '');
  const [busy, setBusy] = useState(false);

  const assignedGroupIds = useMemo(() => new Set(staff.groups.map((g) => g.groupId)), [staff.groups]);
  const availableGroups = groups.filter((g) => !assignedGroupIds.has(g.groupId));

  const wrap = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={`Edit ${staff.displayName}`} onClose={onClose} wide>
      <section className="dialog-section">
        <h3>Details</h3>
        <StaffFormDialog
          title=""
          initial={{
            displayName: staff.displayName,
            givenName: staff.givenName,
            familyName: staff.familyName,
            primaryEmail: staff.primaryEmail,
            status: staff.status,
            notes: staff.notes,
          }}
          onClose={() => {
            /* no-op, parent handles close */
          }}
          onSave={(payload) =>
            wrap(async () => {
              await api.staff.update(staff.staffId, payload);
            })
          }
        />
      </section>

      <section className="dialog-section">
        <h3>Groups</h3>
        <div className="badges">
          {staff.groups.length === 0 && <span className="muted">No groups assigned.</span>}
          {staff.groups.map((g) => (
            <span key={g.groupId} className="badge removable">
              {g.groupName}
              <button
                aria-label={`Remove ${g.groupName}`}
                disabled={busy}
                onClick={() => wrap(() => api.staff.unassignGroup(staff.staffId, g.groupId))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        {availableGroups.length > 0 && (
          <div className="row inline">
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              {availableGroups.map((g) => (
                <option key={g.groupId} value={g.groupId}>
                  {g.groupName}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy || !groupId}
              onClick={() => wrap(() => api.staff.assignGroup(staff.staffId, groupId))}
            >
              Assign
            </button>
          </div>
        )}
      </section>

      <section className="dialog-section">
        <h3>Source links</h3>
        <p className="muted small">
          Map identifiers from each source system to this staff record. Connectors use these to
          attribute activity.
        </p>
        <table className="links-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Identifier</th>
              <th>Linked</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {staff.links.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  No source links yet.
                </td>
              </tr>
            )}
            {staff.links.map((link) => (
              <tr key={`${link.source}/${link.sourceIdentifier}`}>
                <td>{link.source}</td>
                <td className="mono">{link.sourceIdentifier}</td>
                <td>{new Date(link.linkedAt).toLocaleDateString()}</td>
                <td>
                  <button
                    className="secondary"
                    disabled={busy}
                    onClick={() =>
                      wrap(() =>
                        api.staff.removeLink(staff.staffId, link.source, link.sourceIdentifier),
                      )
                    }
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row inline">
          <select value={linkSource} onChange={(e) => setLinkSource(e.target.value as StaffSource)}>
            {SOURCE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            placeholder="Source identifier"
            value={linkId}
            onChange={(e) => setLinkId(e.target.value)}
          />
          <button
            type="button"
            disabled={busy || !linkId.trim()}
            onClick={() =>
              wrap(async () => {
                await api.staff.addLink(staff.staffId, linkSource, linkId.trim());
                setLinkId('');
              })
            }
          >
            Add link
          </button>
        </div>
      </section>

      {error && <p className="error">{error}</p>}

      <section className="dialog-section danger">
        <h3>Danger zone</h3>
        <button
          className="danger"
          disabled={busy}
          onClick={() => {
            if (
              confirm(
                `Delete ${staff.displayName}? This removes all group memberships and source links.`,
              )
            ) {
              void wrap(async () => {
                await api.staff.remove(staff.staffId);
                onClose();
              });
            }
          }}
        >
          Delete staff record
        </button>
      </section>
    </Dialog>
  );
}

function IssuesTab() {
  const [issues, setIssues] = useState<DataQualityIssue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeResolved, setIncludeResolved] = useState(false);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const res = await api.issues.list(includeResolved);
      setIssues(res.issues);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [includeResolved]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div>
      <div className="toolbar">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeResolved}
            onChange={(e) => setIncludeResolved(e.target.checked)}
          />
          Show resolved
        </label>
        <span className="count">{issues ? `${issues.length}` : ''}</span>
      </div>

      {error && <p className="error">{error}</p>}

      {issues && issues.length === 0 && (
        <p className="empty">
          {includeResolved
            ? 'No issues recorded yet.'
            : 'No open issues. Connectors will populate this when they encounter unknown identifiers.'}
        </p>
      )}

      {issues && issues.length > 0 && (
        <table className="staff-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Type</th>
              <th>Identifier</th>
              <th>Detected</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {issues.map((issue) => (
              <tr key={issue.issueId}>
                <td>{issue.source}</td>
                <td>{issue.issueType}</td>
                <td className="mono">{issue.sourceIdentifier ?? '—'}</td>
                <td>{new Date(issue.detectedAt).toLocaleString()}</td>
                <td>
                  {issue.resolvedAt ? (
                    <span className="status status-departed">resolved</span>
                  ) : (
                    <span className="status status-active">open</span>
                  )}
                </td>
                <td>
                  {!issue.resolvedAt && (
                    <button
                      className="secondary"
                      onClick={async () => {
                        const notes = prompt('Resolution notes (optional)');
                        if (notes === null) return;
                        try {
                          await api.issues.resolve(issue.issueId, notes || undefined);
                          await reload();
                        } catch (err) {
                          setError(err instanceof Error ? err.message : String(err));
                        }
                      }}
                    >
                      Resolve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Dialog({
  title,
  children,
  onClose,
  wide,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div
        className={wide ? 'dialog wide' : 'dialog'}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <header className="dialog-header">
            <h2>{title}</h2>
            <button aria-label="Close" className="close" onClick={onClose}>
              ×
            </button>
          </header>
        )}
        <div className="dialog-body">{children}</div>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span>
        {label}
        {required && <em>*</em>}
      </span>
      {children}
    </label>
  );
}

const styles = `
.lede { color: var(--ac-grey-500); max-width: 720px; }
.tab-nav { display: flex; gap: 4px; border-bottom: 1px solid var(--ac-grey-200); margin: 16px 0; }
.tab {
  background: transparent; color: var(--ac-grey-700); border: 0;
  padding: 8px 14px; border-bottom: 2px solid transparent; border-radius: 0;
}
.tab:hover { background: var(--ac-grey-100); }
.tab.active { color: var(--ac-blue-dark); border-bottom-color: var(--ac-blue-dark); background: transparent; }

.toolbar { display: flex; align-items: center; gap: 16px; margin-bottom: 12px; }
.toolbar .count { color: var(--ac-grey-500); font-size: 13px; }
.toolbar .checkbox { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; color: var(--ac-grey-700); }

.staff-table, .links-table { width: 100%; border-collapse: collapse; background: var(--ac-white); border: 1px solid var(--ac-grey-200); border-radius: var(--ac-radius); overflow: hidden; }
.staff-table th, .staff-table td, .links-table th, .links-table td {
  text-align: left; padding: 10px 14px; border-bottom: 1px solid var(--ac-grey-100); font-size: 14px;
}
.staff-table th, .links-table th { background: var(--ac-grey-50); font-weight: 600; color: var(--ac-grey-700); }
.staff-table tr:last-child td, .links-table tr:last-child td { border-bottom: 0; }

.status { padding: 2px 8px; border-radius: 999px; font-size: 12px; text-transform: capitalize; }
.status-active { background: rgba(0, 141, 210, 0.12); color: var(--ac-blue-dark); }
.status-inactive { background: var(--ac-grey-100); color: var(--ac-grey-700); }
.status-departed { background: #fae3e3; color: #7c1f1f; }

.badge { display: inline-block; padding: 2px 8px; margin-right: 4px; background: var(--ac-grey-100); color: var(--ac-grey-700); border-radius: 999px; font-size: 12px; }
.badge.removable { display: inline-flex; align-items: center; gap: 4px; padding-right: 4px; }
.badge.removable button { background: transparent; color: inherit; padding: 0 4px; font-size: 14px; border-radius: 50%; }
.badge.removable button:hover { background: var(--ac-grey-200); color: var(--ac-black); }

.muted { color: var(--ac-grey-500); }
.small { font-size: 13px; }
.mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 13px; }
.empty { color: var(--ac-grey-500); padding: 24px; text-align: center; background: var(--ac-white); border: 1px dashed var(--ac-grey-200); border-radius: var(--ac-radius); }
.error { color: #b00020; background: #fae3e3; padding: 8px 12px; border-radius: var(--ac-radius); }

.dialog-backdrop {
  position: fixed; inset: 0; background: rgba(26, 26, 26, 0.4);
  display: flex; align-items: flex-start; justify-content: center;
  padding: 48px 16px; z-index: 100; overflow-y: auto;
}
.dialog {
  background: var(--ac-white); border-radius: var(--ac-radius);
  box-shadow: var(--ac-shadow-md); width: 100%; max-width: 480px;
}
.dialog.wide { max-width: 720px; }
.dialog-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid var(--ac-grey-200); }
.dialog-header h2 { margin: 0; font-size: 18px; }
.dialog-header .close { background: transparent; color: var(--ac-grey-500); padding: 4px 8px; font-size: 18px; }
.dialog-body { padding: 16px 20px; }
.dialog-section { margin-bottom: 24px; }
.dialog-section h3 { font-size: 14px; font-weight: 600; color: var(--ac-grey-700); margin: 0 0 8px; text-transform: uppercase; letter-spacing: 0.04em; }
.dialog-section.danger { padding-top: 16px; border-top: 1px solid var(--ac-grey-200); }
.badges { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }

.field { display: block; margin-bottom: 12px; font-size: 14px; }
.field > span { display: block; color: var(--ac-grey-700); margin-bottom: 4px; }
.field > span em { color: #b00020; font-style: normal; margin-left: 2px; }
.field input, .field select, .field textarea {
  width: 100%; box-sizing: border-box;
  padding: 8px 10px; border: 1px solid var(--ac-grey-200); border-radius: var(--ac-radius);
  font: inherit;
}
.field input:focus, .field select:focus, .field textarea:focus {
  outline: 2px solid rgba(0, 141, 210, 0.3); border-color: var(--ac-blue-dark);
}
.row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.row.inline { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.row.inline select, .row.inline input { padding: 8px 10px; border: 1px solid var(--ac-grey-200); border-radius: var(--ac-radius); font: inherit; }
.row.inline input { flex: 1 1 200px; }
.actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
button.danger { background: #b00020; }
button.danger:hover { background: #8a0019; }
`;
